import { useState } from 'react';
import { P } from '@stockpilot/shared';
import { useCreateBrandMutation, type Brand } from '@/api/endpoints';
import { apiFieldErrors } from '@/api/baseApi';
import { Input } from '@/components/ui';
import { useAccess } from '@/hooks/useAccess';
import { CreatableSelect } from '@/components/CreatableSelect';

const emptyBrand = { name: '', description: '' };

interface BrandPickerProps {
  label?: string;
  value: string;
  onChange: (brandId: string) => void;
  brands: Brand[];
  hint?: string;
  error?: string;
  required?: boolean;
}

/** A brand dropdown that can create the brand it is missing. */
export function BrandPicker({
  label = 'Brand',
  value,
  onChange,
  brands,
  hint,
  error,
  required,
}: BrandPickerProps) {
  const { can } = useAccess();
  const [createBrand, createState] = useCreateBrandMutation();
  const [form, setForm] = useState(emptyBrand);

  const fieldErrors = apiFieldErrors(createState.error);

  return (
    <CreatableSelect
      label={label}
      noun="brand"
      required={required}
      value={value}
      onChange={onChange}
      hint={hint}
      error={error}
      placeholder="No brand"
      options={brands.map((brand) => ({ id: brand.id, label: brand.name }))}
      canCreate={can(P.CATEGORY_MANAGE)}
      creating={createState.isLoading}
      createDisabled={!form.name}
      onReset={() => setForm(emptyBrand)}
      onCreate={async () => {
        const created = await createBrand({
          name: form.name,
          description: form.description || undefined,
        }).unwrap();
        return created.id;
      }}
      form={
        <div className="grid gap-3 sm:grid-cols-2">
          <Input
            label="Name"
            placeholder="Rwenzori"
            value={form.name}
            onChange={(event) => setForm({ ...form, name: event.target.value })}
            error={fieldErrors.name}
          />
          <Input
            label="Description"
            value={form.description}
            onChange={(event) => setForm({ ...form, description: event.target.value })}
            error={fieldErrors.description}
          />
        </div>
      }
    />
  );
}
